import React, { useEffect, useMemo, useReducer, useRef, useState, Suspense, lazy } from 'react';
import { useAppContext } from '../context/AppContext';
import { authedFetch } from '../ai/authedFetch';
import { fetchObjectives, submitLatticeJob, pollLatticeJob, latticeStlUrl } from '../ai/latticeApi';
import { assessReadiness, deriveEstimateDefaults, mergeUpgradedProps } from '../utils/latticeReadiness';
import { preselectObjective } from '../ai/registry/latticeObjectiveMap';
import { latticeFlowReducer, initialFlowState } from '../hooks/useLatticeFlow';
import { registerJob, getJob, clearJob } from '../utils/dftJobRegistry';
import { findJobsForCandidate } from '../utils/dftJobLookup';
import { useElapsed } from '../hooks/useElapsed';
import { useJobLookupScope } from '../hooks/useCapability';
import { capabilityDeniedMessage } from '../utils/capabilityErrors';

// Lazy-loaded: StlViewer pulls in three/examples/jsm/* (native ESM), which the
// project's plain `npx jest` config (transformIgnorePatterns: ['/node_modules/'])
// cannot transform. Static-importing it here would make three-and-friends part
// of AppShell's synchronous module graph, breaking every test that merely
// imports AppShell.jsx (e.g. tests/ai-discovery-engine/crystalCifArtifactAuth.test.js)
// even though those tests never render the wizard. Deferring via React.lazy keeps
// the import graph CJS-safe for tests while still code-splitting the real
// component out of the main bundle for the browser build.
const StlViewer = lazy(() => import('./StlViewer'));

const CYAN = '#22d3ee';
const LABEL_STYLE = { color: '#94a3b8' };
const SOURCE_COLOR = { user: '#4ade80', dft: '#38bdf8', estimate: '#f59e0b' };

const PRIMARY_BTN = { background: CYAN, color: '#04222b', border: 'none', borderRadius: 6, padding: '8px 16px', fontWeight: 700, cursor: 'pointer' };
const SECONDARY_BTN = { background: 'transparent', color: CYAN, border: `1px solid ${CYAN}`, borderRadius: 6, padding: '8px 16px', fontWeight: 600, cursor: 'pointer' };

const CONTRACT_KEYS = [
  'density_g_cc', 'youngs_modulus_gpa', 'shear_modulus_gpa', 'poisson',
  'compressive_strength_mpa', 'elongation', 'fracture_toughness_mpa_m05', 'speed_of_sound_ms',
];

// Props the elastic tensor DFT job (dft_elastic_tensor, the "Run DFT, then
// generate" path) actually computes. Used to decide whether the missing-props
// state can offer that CTA, and to hint each not-set field in the property
// table with how it gets filled in.
const DFT_DERIVABLE = ['youngs_modulus_gpa', 'shear_modulus_gpa', 'poisson', 'speed_of_sound_ms'];
const DFT_JOB_EXPLANATION = "Runs the Elastic Tensor DFT job on this candidate to compute Young's modulus, shear modulus, Poisson's ratio, and speed of sound. Takes a while.";
const DFT_FIELD_HINT = 'computed by the Elastic Tensor DFT run';
const MANUAL_FIELD_HINT = 'enter a value';

const KEY_LABEL = {
  density_g_cc: 'Density (g/cc)',
  youngs_modulus_gpa: "Young's modulus (GPa)",
  shear_modulus_gpa: 'Shear modulus (GPa)',
  poisson: "Poisson's ratio",
  compressive_strength_mpa: 'Compressive strength (MPa)',
  elongation: 'Elongation at break',
  fracture_toughness_mpa_m05: 'Fracture toughness (MPa m^0.5)',
  speed_of_sound_ms: 'Speed of sound (m/s)',
};

const PHYSICS_BASIS_TEXT = {
  gibson_ashby: 'Modeled with Gibson-Ashby scaling laws for the chosen lattice family, calibrated per topology.',
  progressive_failure: 'Modeled with a layer by layer progressive failure impact simulation.',
  phonon_bandgap: 'Modeled with phonon band gap physics.',
  fea_homogenization: 'Modeled with finite element homogenization.',
};

const HEADLINE_SENTENCES = {
  impact_protection: (m) => (m.energy_absorbed_j != null && m.mass_g != null)
    ? `Absorbs ${round(m.energy_absorbed_j)} J at ${round(m.mass_g)} g${m.stopped ? ', stopping the impactor' : ''}.`
    : null,
  stiffness_to_weight: (m) => (m.effective_youngs_mpa != null && m.mass_g != null)
    ? `Delivers ${round(m.effective_youngs_mpa)} MPa effective stiffness at ${round(m.mass_g)} g${m.weight_saving_pct != null ? ` (${round(m.weight_saving_pct)}% lighter than solid)` : ''}.`
    : null,
  crush_strength: (m) => (m.effective_strength_mpa != null && m.mass_g != null)
    ? `Withstands ${round(m.effective_strength_mpa)} MPa before collapse at ${round(m.mass_g)} g.`
    : null,
  cushioning: (m) => (m.energy_to_densification_j != null && m.mass_g != null)
    ? `Absorbs ${round(m.energy_to_densification_j)} J before densifying, at ${round(m.mass_g)} g.`
    : null,
  heat_insulation: (m) => (m.relative_conductivity != null)
    ? `Conducts heat at ${round(m.relative_conductivity * 100)}% of the solid material's rate.`
    : null,
};

function round(n) { return typeof n === 'number' ? Math.round(n * 100) / 100 : n; }
function numOr(v, d) { const n = Number(v); return v !== '' && v != null && Number.isFinite(n) ? n : d; }

// latticeJobs' submitted_at (models/lattice-service/jobs.py's create()) is
// time.time() epoch seconds, a plain number once round-tripped through
// Firestore -- unlike dftJobs' submitted_at, which is an ISO string
// (dft-service's _now_iso()) and reads fine with a bare Date.parse. Handles
// both so a future format change on either side degrades gracefully instead
// of silently producing an elapsed timer stuck at "0m".
function parseSubmittedAt(v) {
  if (typeof v === 'number') return v * 1000;
  if (typeof v === 'string') { const t = Date.parse(v); return Number.isNaN(t) ? null : t; }
  return null;
}

function buildConstraintsBody(constraints, objective) {
  const body = {
    footprint_x_mm: numOr(constraints.footprint_x_mm, 100),
    footprint_y_mm: numOr(constraints.footprint_y_mm, 100),
    thickness_range_mm: [numOr(constraints.thickness_lo_mm, 10), numOr(constraints.thickness_hi_mm, 30)],
    population: numOr(constraints.population, 40),
    generations: numOr(constraints.generations, 25),
  };
  if (objective?.id === 'impact_protection') {
    body.impact_energy_j = numOr(constraints.impact_energy_j, 50);
    body.impactor_diameter_mm = numOr(constraints.impactor_diameter_mm, 5.6);
  }
  if (objective?.id === 'heat_insulation' && constraints.min_stiffness_mpa !== '' && constraints.min_stiffness_mpa != null) {
    body.min_stiffness_mpa = Number(constraints.min_stiffness_mpa);
  }
  if (objective?.id === 'cushioning' && constraints.stiffness_ceiling_mpa !== '' && constraints.stiffness_ceiling_mpa != null) {
    body.stiffness_ceiling_mpa = Number(constraints.stiffness_ceiling_mpa);
  }
  return body;
}

const DEFAULT_CONSTRAINTS = {
  footprint_x_mm: 100, footprint_y_mm: 100,
  thickness_lo_mm: 10, thickness_hi_mm: 30,
  impact_energy_j: 50, impactor_diameter_mm: 5.6,
  min_stiffness_mpa: '', stiffness_ceiling_mpa: '',
  population: 40, generations: 25,
};

export default function LatticeWizard({ candidate, candidateFingerprint, searchObjectives, onClose, onDftPollUpdate }) {
  const { defaultLatticeUrl, dftBaseUrl, byocActive, byocAuthHeaders } = useAppContext() || {};
  const baseUrl = defaultLatticeUrl || 'http://localhost:8016';
  const dftUrl = dftBaseUrl || 'http://localhost:8013';
  // Same pairing as DftAsyncPanel / submitDftJob in AppShell.jsx: when BYOC is
  // active, dftBaseUrl already points at the relay (${proxyBaseUrl}/relay/dft),
  // so every dftUrl-based fetch needs the caller's relay headers attached.
  const dftAuthHdrs = byocActive ? byocAuthHeaders : undefined;
  // Registry key for the one DFT job kind this wizard's own chain submits.
  // Null when the caller (a test, or a candidate somehow missing lattice
  // identity) didn't supply a fingerprint -- every registry interaction below
  // guards on this and simply behaves as it did before the registry existed.
  const dftRegistryKey = candidateFingerprint ? `${candidateFingerprint}::dft_elastic_tensor` : null;

  const [objectives, setObjectives] = useState([]);
  const [objectivesError, setObjectivesError] = useState(null);
  // Starts true: on a genuine cold open (nothing cached yet) fetchObjectives'
  // GET can take several seconds against a scale-to-zero lattice-service
  // instance, and this drives the skeleton below so that wait never reads as
  // a frozen modal. A cache hit (the common case once AppShell's prefetch on
  // the results table has had a chance to run) resolves fast enough that
  // this flips back to false almost immediately.
  const [objectivesLoading, setObjectivesLoading] = useState(true);
  const [selectedScale, setSelectedScale] = useState('macro');
  const [selectedObjectiveId, setSelectedObjectiveId] = useState(null);
  const [userEdits, setUserEdits] = useState({});
  const [constraints, setConstraints] = useState(DEFAULT_CONSTRAINTS);
  const [advancedOpen, setAdvancedOpen] = useState(false);
  const [state, dispatch] = useReducer(latticeFlowReducer, initialFlowState);
  // Elapsed-timer start times (Fix 3): set once, when each phase actually
  // begins (fresh submit) or, for the DFT phase, backdated to the job's real
  // submittedAt when resuming an in-flight job via the registry -- so a
  // reopened wizard's timer reflects how long the job has really been
  // running rather than resetting to zero.
  const [dftStartedAt, setDftStartedAt] = useState(null);
  const [latticeStartedAt, setLatticeStartedAt] = useState(null);

  // #41-style ref: onDftPollUpdate must not be a dependency of the dft_running
  // effect below (AppShell passes a fresh closure every render), or the
  // effect would tear down and resubmit the DFT job on every unrelated parent
  // re-render while it's running.
  const onDftPollUpdateRef = useRef(onDftPollUpdate);
  useEffect(() => { onDftPollUpdateRef.current = onDftPollUpdate; }, [onDftPollUpdate]);

  // Resolved once by the mount effect below (Firestore lookup, falling back
  // to the localStorage registry): { jobId, submittedAt, autoGenerate } for
  // whichever in-flight/completed dft_elastic_tensor job this candidate
  // already has, or null if there isn't one. A ref (not state) because the
  // dft_running poll effect further below reads it synchronously, on the
  // same render DFT_STARTED flips state.phase -- state wouldn't be visible
  // there until a render later.
  const resumedDftJobRef = useRef(null);

  // Review gate (Important, pre-deploy): true while the mount effect below
  // is still awaiting its Firestore lookup (or the localStorage registry
  // fallback) for an existing dft_elastic_tensor job. Until it resolves,
  // GateRow renders the "Run DFT, then generate" CTA disabled instead of
  // clickable -- otherwise a user on a fresh browser/session (nothing in
  // the registry yet, but a real job already sitting in Firestore) could
  // click it mid-lookup and submit a second, duplicate, expensive GPAW run
  // before the resume had a chance to find the existing one, exactly the
  // cross-device scenario this durable index exists to prevent. Starts
  // true only when there is something to actually await
  // (dftRegistryKey present); a candidate with no lattice identity at all
  // behaves exactly as it always has.
  const [dftResumeChecking, setDftResumeChecking] = useState(!!dftRegistryKey);

  // Review gate (D5, durability audit 2026-08-20): true while the mount
  // effect below is still awaiting its Firestore lookup for an existing
  // latticeJobs doc for this candidate. Mirrors dftResumeChecking exactly,
  // gating the ready-path "Generate lattice" CTA and the estimate-grade
  // "Generate with estimates" CTA in GateRow the same way dftResumeChecking
  // already gates "Run DFT, then generate" -- otherwise a user on a fresh
  // browser/session could click Generate mid-lookup and submit a second,
  // duplicate, expensive GA run before the resume had a chance to find the
  // real one. Starts true only when there is something to actually await.
  const [latticeResumeChecking, setLatticeResumeChecking] = useState(!!candidateFingerprint);

  // Org-scoped resume (2026-08-22): read through a ref so the one-shot
  // resume effects below never re-fire when the auth context re-renders.
  const lookupScope = useJobLookupScope();
  const lookupScopeRef = useRef(lookupScope);
  lookupScopeRef.current = lookupScope;

  // Resume-on-reopen: if a dft_elastic_tensor job is already in flight (or
  // even already finished) for this candidate (this wizard, or AppShell's
  // Analyze panel, submitted it before this modal was closed and remounted
  // -- LatticeWizard always fully unmounts on close, so a fresh mount
  // otherwise has no memory of it), jump straight into the dft_running
  // phase. The poll effect below then reads resumedDftJobRef (set here) to
  // resume polling that job id instead of the objective picker inviting a
  // second, duplicate, expensive GPAW run. Mount-only by design (empty
  // deps): state.phase is deliberately excluded so this can't refire once
  // DFT_COMPLETE/DFT_FAILED route back through 'configuring'.
  //
  // Resume order: Firestore (durable, survives a different browser/profile/
  // device -- see src/utils/dftJobLookup.js) first, the localStorage
  // dftJobRegistry fallback second, for when Firebase isn't configured.
  // Either source's autoGenerate carries the ORIGINAL submitter's intent
  // (true only for a job this wizard's own dft_first path started) through
  // to the reducer, which sets autoSubmitAfterDft from it: resuming must
  // neither silently drop the "then generate" chain on a job this wizard
  // itself started, nor auto-fire a lattice run on a job the Analyze panel
  // submitted standalone that the user never asked this wizard to chain
  // into.
  useEffect(() => {
    if (!dftRegistryKey) return;
    let cancelled = false;
    (async () => {
      try {
        let resumed = null;
        if (candidateFingerprint) {
          const jobs = await findJobsForCandidate('dftJobs', candidateFingerprint, lookupScopeRef.current);
          const match = jobs.find((j) => j?.payload?.type === 'elastic_tensor');
          if (match?.job_id) {
            resumed = {
              jobId: match.job_id,
              submittedAt: match.submitted_at ? Date.parse(match.submitted_at) : null,
              autoGenerate: !!match.auto_generate,
            };
          }
        }
        if (!resumed) {
          const job = getJob(dftRegistryKey);
          if (job?.jobId) resumed = { jobId: job.jobId, submittedAt: job.submittedAt, autoGenerate: !!job.autoGenerate };
        }
        if (cancelled || !resumed) return;
        resumedDftJobRef.current = resumed;
        dispatch({ type: 'DFT_STARTED', autoGenerate: resumed.autoGenerate });
      } finally {
        // Runs on every exit above (found, not found, or an unexpected
        // throw) -- the dft_first CTA must never stay disabled forever.
        if (!cancelled) setDftResumeChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Resume-on-reopen for the GA/generate step itself (D5, durability audit
  // 2026-08-20). Server-side, models/lattice-service writes the latticeJobs
  // Firestore doc the same write-before-compute way dft-service writes
  // dftJobs (jobs.create calls _mirror before main.py's _drive task is even
  // created, see jobs.py:468-496/main.py:174-217) -- but until this effect
  // existed, the wizard never read it back. Closing the browser mid-GA-run
  // left a genuinely durable doc while a reopened wizard showed no sign of
  // it, risking a duplicate, expensive GA resubmission exactly like the
  // dft_elastic_tensor gap the mount effect above already closes for the
  // DFT step. Mirrors that effect's shape: mount-only (empty deps,
  // state.phase deliberately excluded so this can't refire once
  // LATTICE_COMPLETE/LATTICE_FAILED route back through 'configuring'),
  // Firestore-only (there is no localStorage registry for this job kind to
  // fall back to, unlike dftJobRegistry for the DFT step).
  //
  // A running/queued match resumes via the SAME LATTICE_SUBMITTED action a
  // fresh submission already dispatches (jobId set, phase -> 'generating'):
  // the existing GA-progress poll effect below keys only on state.phase /
  // state.jobId, not on how they got set, so it starts polling the resumed
  // job id with no other change needed -- exactly mirroring how the
  // dft_running effect above doesn't care whether resumedDftJobRef or a
  // fresh POST supplied its job id. A complete match hydrates the result
  // panel directly via LATTICE_COMPLETE (the result is already in hand from
  // the Firestore doc; polling a job that's already finished would just be
  // a wasted round trip). An error/cancelled match has nothing durable worth
  // resuming into: the wizard is left on the objective picker exactly as if
  // no job existed, same as a missing match.
  useEffect(() => {
    if (!candidateFingerprint) return;
    let cancelled = false;
    (async () => {
      try {
        const jobs = await findJobsForCandidate('latticeJobs', candidateFingerprint, lookupScopeRef.current);
        const match = jobs[0];
        if (cancelled || !match?.job_id) return;
        // request.objective (models/lattice-service/main.py's submit_job,
        // jobs.create's request dict) identifies which objective this run
        // used -- ResultPanel's headline sentence and the CTA labels below
        // key off selectedObjectiveId, and resume must show the SAME
        // objective the job actually ran under, not whatever the preselect
        // effect elsewhere in this file happens to land on.
        if (match.request?.objective) setSelectedObjectiveId(match.request.objective);
        if (match.status === 'complete') {
          dispatch({ type: 'LATTICE_COMPLETE', result: match.result });
        } else if (match.status === 'running' || match.status === 'queued') {
          const submittedAt = parseSubmittedAt(match.submitted_at) || Date.now();
          setLatticeStartedAt(submittedAt);
          dispatch({ type: 'LATTICE_SUBMITTED', jobId: match.job_id });
        }
      } finally {
        // Runs on every exit above (found, not found, or an unexpected
        // throw) -- the Generate CTAs must never stay disabled forever.
        if (!cancelled) setLatticeResumeChecking(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    let cancelled = false;
    setObjectivesLoading(true);
    fetchObjectives(baseUrl)
      .then((data) => { if (!cancelled) setObjectives(Array.isArray(data?.objectives) ? data.objectives : []); })
      .catch((e) => { if (!cancelled) setObjectivesError(e.message); })
      .finally(() => { if (!cancelled) setObjectivesLoading(false); });
    return () => { cancelled = true; };
  }, [baseUrl]);

  useEffect(() => {
    if (objectives.length === 0 || selectedObjectiveId) return;
    const pre = preselectObjective(searchObjectives);
    if (pre && objectives.some((o) => o.id === pre && o.scale === 'macro' && o.status === 'live')) {
      setSelectedObjectiveId(pre);
    }
  }, [objectives, searchObjectives, selectedObjectiveId]);

  const selectedObjective = objectives.find((o) => o.id === selectedObjectiveId) || null;
  // Resume (DFT_STARTED, mount effect above) can enter dft_running before
  // objectives have finished loading and the preselect effect above has had
  // a chance to set selectedObjectiveId -- unlike the GENERATE_CLICKED
  // dft_first path, whose CTA only ever renders once selectedObjective is
  // already non-null, so it was never at risk of this. The dft_running
  // effect below deliberately excludes selectedObjective from its deps (so
  // it doesn't cancel and resubmit the job on every objective change), which
  // means its poll closure would otherwise capture whatever selectedObjective
  // was at the moment the effect was CREATED and never see it get set
  // afterward -- silently keeping dftReadiness null at DFT_COMPLETE time and
  // skipping the I4 consent gate's missing/estimate-grade checks entirely.
  // Read through a ref instead so the completion handler always sees the
  // current value.
  const selectedObjectiveRef = useRef(selectedObjective);
  useEffect(() => { selectedObjectiveRef.current = selectedObjective; }, [selectedObjective]);
  // Precedence: candidate.props + userEdits form the base (a genuine user
  // edit always applies), then state.upgradedProps (DFT chaining results)
  // overlays it via mergeUpgradedProps, which only lets a DFT value win over
  // a base entry that is null or not really user-provided. Naively spreading
  // state.upgradedProps then userEdits last (the old code here) let a
  // cleared field's stale { value: null, source: 'user' } permanently
  // shadow a fresh DFT result: this MUST use the same precedence as the
  // DFT_COMPLETE handler below, or the readiness a submission gets gated on
  // can disagree with what material_props actually submits.
  const props = useMemo(
    () => mergeUpgradedProps({ ...(candidate?.props || {}), ...userEdits }, state.upgradedProps || {}),
    [candidate?.props, state.upgradedProps, userEdits],
  );
  const readiness = selectedObjective ? assessReadiness(selectedObjective, props) : null;
  // The property table (and its per-field fill hints) lives behind "Show
  // advanced" by default, but a missing-props state has nothing else to act
  // on: force it open so "type values into the table" in the guidance below
  // isn't pointing at something hidden. The user's own toggle still applies
  // once nothing required is missing.
  const showAdvanced = advancedOpen || (readiness?.missing?.length > 0);

  const editProp = (key, value) => setUserEdits((prev) => ({ ...prev, [key]: { value, source: 'user', confidence: 1.0 } }));

  // DFT chaining: while the wizard is in dft_running phase, submit and poll the
  // elastic tensor job, then upgrade the property table and (if the user came
  // in via the "Run DFT, then generate" path) auto-submit the lattice job.
  useEffect(() => {
    if (state.phase !== 'dft_running') return undefined;
    let cancelled = false;
    let pollId = null;

    const poll = (jobId) => {
      pollId = setInterval(async () => {
        try {
          const res = await authedFetch(`${dftUrl}/analyze/crystal/dft_job/${jobId}`, { headers: dftAuthHdrs });
          const data = await res.json();
          if (cancelled) return;
          // Fix 1: the wizard's own poll may be the surface that observes any
          // given tick (including the terminal one). Feed every result through
          // the SAME analysisCache write + Firestore persist path AppShell's
          // own DftAsyncPanel poll uses (see AppShell.jsx's handleDftPollUpdate),
          // regardless of status, so the Analyze panel and buildLatticeProps
          // stay in sync with whatever this poll just learned -- including the
          // elastic-tensor deformation progress (Fix 2) while still running.
          onDftPollUpdateRef.current?.(data);
          if (data.status === 'complete') {
            clearInterval(pollId);
            if (dftRegistryKey) clearJob(dftRegistryKey);
            const upgraded = {};
            if (data.youngs_modulus_gpa != null) upgraded.youngs_modulus_gpa = { value: data.youngs_modulus_gpa, source: 'dft', confidence: 0.8 };
            if (data.shear_modulus_gpa != null) upgraded.shear_modulus_gpa = { value: data.shear_modulus_gpa, source: 'dft', confidence: 0.8 };
            if (data.poisson_ratio != null) upgraded.poisson = { value: data.poisson_ratio, source: 'dft', confidence: 0.8 };
            if (data.speed_of_sound_ms != null) upgraded.speed_of_sound_ms = { value: data.speed_of_sound_ms, source: 'dft', confidence: 0.8 };
            // Resolve the actual post-DFT prop set FIRST, with the same
            // precedence as the live `props` useMemo above (mergeUpgradedProps:
            // a fresh DFT value beats a stale/cleared user edit -- a userEdits
            // entry of { value: null, source: 'user' }, e.g. the user typed a
            // value then cleared the field -- but a genuine non-null user
            // edit still wins over DFT). Reading youngs_modulus_gpa, deriving
            // from it, and assessing readiness must all happen against THIS
            // resolved set: doing it against a naive
            // { ...candidate.props, ...upgraded, ...userEdits } spread (userEdits
            // unconditionally last) let a stale null silently shadow the fresh
            // DFT result, so youngsGpa read null, the derive step below never
            // ran, and readiness came back with a required prop still
            // `missing` instead of resolved -- which the reducer didn't even
            // gate on (see useLatticeFlow.js DFT_COMPLETE), so it could
            // auto-submit with that prop still null.
            const baseProps = { ...(candidate?.props || {}), ...userEdits };
            let finalProps = mergeUpgradedProps(baseProps, upgraded);
            // The elastic tensor DFT job only upgrades youngs/shear/poisson/
            // speed_of_sound. Objectives that also require strength,
            // elongation, or fracture toughness (impact protection, crush
            // strength, cushioning) can still be sitting on estimate-grade,
            // or even still-missing, values for those after this lands (a
            // candidate that arrived with no bulk modulus at all never got
            // those derived by buildLatticeProps in the first place). Derive
            // the same estimate-grade defaults buildLatticeProps would from
            // the fresh Young's modulus for whichever of those are still
            // unset, so the objective's required props land on estimateGrade
            // rather than missing and the existing consent dialog takes over
            // instead of a second dead end.
            const youngsGpa = finalProps.youngs_modulus_gpa?.value;
            if (youngsGpa != null) {
              const derived = deriveEstimateDefaults(finalProps, youngsGpa);
              for (const key of ['compressive_strength_mpa', 'elongation', 'fracture_toughness_mpa_m05']) {
                if (finalProps[key] == null && derived[key] != null) {
                  upgraded[key] = derived[key];
                  finalProps = { ...finalProps, [key]: derived[key] };
                }
              }
            }
            // Hand the resolved readiness to the reducer as action.readiness
            // so an auto-submit path can't silently skip the consent dialog
            // (or, per the DFT_COMPLETE reducer's missing-props check, submit
            // outright with a required prop still unresolved). Read via
            // selectedObjectiveRef, not the closed-over selectedObjective --
            // see its declaration above for why (resume can enter dft_running
            // before an objective is selected, and this effect never re-fires
            // once it has to pick up a later selection).
            const currentObjective = selectedObjectiveRef.current;
            const dftReadiness = currentObjective ? assessReadiness(currentObjective, finalProps) : null;
            dispatch({ type: 'DFT_COMPLETE', props: upgraded, readiness: dftReadiness });
          } else if (data.status === 'error' || data.status === 'cancelled') {
            clearInterval(pollId);
            if (dftRegistryKey) clearJob(dftRegistryKey);
            dispatch({ type: 'DFT_FAILED', error: data.error || 'The DFT job failed.' });
          }
        } catch (e) {
          if (!cancelled) { clearInterval(pollId); dispatch({ type: 'DFT_FAILED', error: e.message }); }
        }
      }, 5000);
    };

    (async () => {
      try {
        // Fix 1: a job may already be in flight for this candidate -- either
        // this same wizard submitted it before an earlier close (the mount
        // effect above jumped straight to dft_running for exactly that case),
        // or AppShell's Analyze panel did. Either way, resume polling the
        // existing job instead of submitting a second, duplicate, expensive
        // GPAW/QE run. resumedDftJobRef, set by the mount effect's Firestore-
        // first lookup, takes priority; the localStorage registry is only
        // consulted as its fallback (a fresh dft_first submission, the
        // common case, finds neither and falls through to the POST below).
        const existing = resumedDftJobRef.current || (dftRegistryKey ? getJob(dftRegistryKey) : null);
        if (existing?.jobId) {
          const resumedAt = existing.submittedAt || Date.now();
          setDftStartedAt(resumedAt);
          // Optimistic write, same as AppShell's own submitDftJob: don't wait
          // for the first real poll tick (up to 5s away) before analysisCache
          // reflects that this job is in flight, in case the Analyze panel is
          // opened in the meantime.
          onDftPollUpdateRef.current?.({ status: 'queued', job_id: existing.jobId, queued_at: resumedAt });
          poll(existing.jobId);
          return;
        }
        const startedAt = Date.now();
        const res = await authedFetch(`${dftUrl}/analyze/crystal/dft_elastic_tensor`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', ...dftAuthHdrs },
          // candidate_fingerprint + auto_generate land on the job doc
          // server-side (models/dft-service/main.py) so a resuming UI can
          // find this job via Firestore (src/utils/dftJobLookup.js) on a
          // fresh browser/profile, not just via the registerJob call below.
          // auto_generate is always true here: this branch only runs for a
          // job THIS wizard is originating via its own dft_first chain (see
          // the comment on the registerJob call below).
          body: JSON.stringify({ cif: candidate?.cif, candidate_fingerprint: candidateFingerprint, auto_generate: true }),
        });
        const data = await res.json();
        if (!res.ok || !data.job_id) {
          // Nothing was actually accepted server-side (or the response was
          // malformed) -- report failure only if there's a mounted wizard
          // left to show it.
          if (!cancelled) throw new Error(capabilityDeniedMessage(data) || (typeof data.detail === 'string' ? data.detail : '') || 'Could not start the DFT job.');
          return;
        }
        // The server has accepted and started this job -- this is the exact
        // 200 a bug report traced to production logs. Record it regardless
        // of `cancelled`: dft-service is scale-to-zero (cold start can push
        // this POST's own response past the point where an impatient user
        // has already closed the wizard, unmounting it before this line
        // ever ran under the old ordering), and neither write below is
        // unsafe to make after that. registerJob is a plain localStorage
        // side effect, never tied to this component's lifecycle. The
        // onDftPollUpdate callback writes into AppShell's analysisCache --
        // the PARENT's state, not this wizard's -- and the parent (the
        // candidate's row) stays mounted across the wizard closing. Doing
        // these after the old `if (cancelled) return;` silently dropped a
        // job the server had already started: the shared registry never
        // learned about it, so the Analyze panel had no way to resume it
        // and fell back to showing "Submit DFT Job" as if nothing were
        // running.
        //
        // This branch is only ever reached for a job THIS wizard is
        // originating (the dft_first CTA, or the rare race where a
        // resume-triggered mount found the registry entry already gone by
        // the time this effect re-checked it) -- AppShell's Analyze panel
        // registers its own submissions separately, without autoGenerate.
        if (dftRegistryKey) registerJob(dftRegistryKey, data.job_id, { autoGenerate: true });
        onDftPollUpdateRef.current?.({ status: 'queued', job_id: data.job_id, queued_at: startedAt });
        // Everything past this point (this wizard's OWN local state and its
        // own poll loop) really is unsafe/pointless once unmounted: a fresh
        // mount later on will resume from the registry entry just written,
        // and DftAsyncPanel runs its own independent poll once it does.
        if (cancelled) return;
        setDftStartedAt(startedAt);
        poll(data.job_id);
      } catch (e) {
        if (!cancelled) dispatch({ type: 'DFT_FAILED', error: e.message });
      }
    })();

    return () => { cancelled = true; if (pollId) clearInterval(pollId); };
    // candidate?.props / selectedObjective / userEdits are read only inside the
    // completion branch to compute the post-DFT readiness snapshot; they must
    // NOT retrigger this effect (that would cancel and resubmit the DFT job
    // on every property edit or objective change while it's running).
    // onDftPollUpdate is read via onDftPollUpdateRef for the same reason
    // (AppShell passes a fresh closure every render).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.phase, dftUrl, candidate?.cif, dftAuthHdrs, dftRegistryKey]);

  // Submission: fires once GENERATE_CLICKED / CONSENT_GIVEN sets pendingSubmit.
  // D5 review fix (durability audit 2026-08-20, batch C review IMPORTANT-2):
  // additional defense-in-depth gate alongside the DFT_COMPLETE reducer fix
  // above -- never fire submitLatticeJob while a GA job for this candidate
  // is already in flight (phase 'generating') or already finished (phase
  // 'complete'), regardless of which action set pendingSubmit true.
  useEffect(() => {
    if (!state.pendingSubmit || !selectedObjective || state.phase === 'generating' || state.phase === 'complete') return undefined;
    let cancelled = false;
    (async () => {
      try {
        const body = {
          material_props: props,
          scale: 'macro',
          objective: selectedObjective.id,
          constraints: buildConstraintsBody(constraints, selectedObjective),
        };
        const res = await submitLatticeJob(baseUrl, body);
        if (!cancelled) {
          setLatticeStartedAt(Date.now());
          dispatch({ type: 'LATTICE_SUBMITTED', jobId: res.job_id });
        }
      } catch (e) {
        if (!cancelled) dispatch({ type: 'LATTICE_FAILED', error: e.message });
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.pendingSubmit]);

  // Progress polling for the lattice GA job itself.
  useEffect(() => {
    if (state.phase !== 'generating' || !state.jobId) return undefined;
    let cancelled = false;
    const id = setInterval(async () => {
      try {
        const data = await pollLatticeJob(baseUrl, state.jobId);
        if (cancelled) return;
        if (data.status === 'complete') {
          clearInterval(id);
          dispatch({ type: 'LATTICE_COMPLETE', result: data.result });
        } else if (data.status === 'error' || data.status === 'cancelled') {
          clearInterval(id);
          dispatch({ type: 'LATTICE_FAILED', error: data.error || 'The lattice job failed.' });
        } else if (data.progress) {
          dispatch({ type: 'LATTICE_PROGRESS', progress: data.progress });
        }
      } catch (e) {
        if (!cancelled) { clearInterval(id); dispatch({ type: 'LATTICE_FAILED', error: e.message }); }
      }
    }, 3000);
    return () => { cancelled = true; clearInterval(id); };
  }, [state.phase, state.jobId, baseUrl]);

  const preCommit = state.phase === 'configuring' || state.phase === 'awaiting_consent';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 10000 }}>
      <div style={{ background: '#0d1117', border: `1px solid ${CYAN}55`, borderRadius: 12, width: '95%', maxWidth: 900, maxHeight: '90vh', overflow: 'auto', color: '#e2e8f0' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '14px 20px', borderBottom: `1px solid ${CYAN}33` }}>
          <div style={{ color: CYAN, fontWeight: 700, fontSize: 16 }}>Generate lattice</div>
          <button className="btn btn-outline-light btn-sm" onClick={onClose}>Close</button>
        </div>
        <div style={{ padding: 20 }}>
          {state.error && <div style={{ color: '#f87171', fontSize: 13, marginBottom: 12 }}>{state.error}</div>}
          {objectivesError && <div style={{ color: '#f87171', fontSize: 12, marginBottom: 12 }}>Could not load lattice objectives: {objectivesError}</div>}

          {preCommit && (
            <>
              {/* The scale row's coming-soon labels are themselves derived from
                  `objectives`, so a true cold open (no cache, no prefetch head
                  start) would otherwise show a real Macro card next to blank
                  Nano/Micro labels while the objective grid below sat empty --
                  looking broken rather than loading. Gate both behind the same
                  skeleton so a slow fetch reads as "loading", never "frozen". */}
              {objectivesLoading && objectives.length === 0 ? (
                <ObjectivesSkeleton />
              ) : (
                <>
                  <ScaleStep objectives={objectives} selectedScale={selectedScale} onSelectScale={setSelectedScale} />
                  <ObjectiveStep objectives={objectives} selectedId={selectedObjectiveId} onSelect={setSelectedObjectiveId} />
                </>
              )}

              {selectedObjective && state.phase === 'configuring' && (
                <>
                  <ConfigureStep objective={selectedObjective} constraints={constraints} onChange={setConstraints} />
                  <button
                    onClick={() => setAdvancedOpen((o) => !o)}
                    style={{ background: 'transparent', border: 'none', color: CYAN, fontSize: 12, cursor: 'pointer', padding: 0, marginTop: 6 }}
                  >
                    {showAdvanced ? 'Hide advanced' : 'Show advanced'}
                  </button>
                  {showAdvanced && (
                    <div style={{ marginTop: 10 }}>
                      <div style={{ display: 'flex', gap: 12 }}>
                        <NumberField label="Population" value={constraints.population} onChange={(e) => setConstraints({ ...constraints, population: e.target.value })} />
                        <NumberField label="Generations" value={constraints.generations} onChange={(e) => setConstraints({ ...constraints, generations: e.target.value })} />
                      </div>
                      <PropertyTable props={props} onEdit={editProp} missingKeys={readiness?.missing} />
                    </div>
                  )}
                  <GateRow
                    readiness={readiness}
                    onReady={() => dispatch({ type: 'GENERATE_CLICKED', readiness, path: 'ready' })}
                    onDftFirst={() => dispatch({ type: 'GENERATE_CLICKED', readiness, path: 'dft_first' })}
                    onEstimates={() => dispatch({ type: 'GENERATE_CLICKED', readiness, path: 'estimates' })}
                    dftFirstChecking={dftResumeChecking}
                    latticeResumeChecking={latticeResumeChecking}
                  />
                </>
              )}

              {state.phase === 'awaiting_consent' && (
                <ConsentDialog
                  estimateGrade={readiness?.estimateGrade || []}
                  onProceed={() => dispatch({ type: 'CONSENT_GIVEN' })}
                  onCancel={() => dispatch({ type: 'CONSENT_DECLINED' })}
                />
              )}
            </>
          )}

          {(state.phase === 'dft_running' || state.phase === 'generating') && (
            <ProgressView
              phase={state.phase}
              progress={state.progress}
              startedAt={state.phase === 'dft_running' ? dftStartedAt : latticeStartedAt}
            />
          )}

          {state.phase === 'complete' && (
            <ResultPanel result={state.result} objectiveId={selectedObjectiveId} baseUrl={baseUrl} jobId={state.jobId} props={props} />
          )}

          {state.phase === 'failed' && (
            <div style={{ marginTop: 12 }}>
              <div style={{ color: '#f87171', fontSize: 13 }}>{state.error || 'The run failed.'}</div>
              <button className="btn btn-outline-light btn-sm mt-2" onClick={() => dispatch({ type: 'RESET' })}>Start over</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Placeholder for the Scale + Objective area while fetchObjectives is in
// flight. Uses Bootstrap's placeholder/placeholder-glow classes (already
// bundled, see LatticeWizard.jsx imports and app-wide bootstrap dependency)
// for the pulse animation, so no extra keyframes are needed here. Card
// counts and rough sizing mirror the real ScaleStep/ObjectiveStep layout so
// the wizard doesn't visibly reflow once the real content lands.
function ObjectivesSkeleton() {
  const cardStyle = { flex: 1, padding: '14px 16px', borderRadius: 10, border: '1px solid #334155', background: '#0a1120' };
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={LABEL_STYLE}>Scale</div>
      <div className="placeholder-glow" style={{ display: 'flex', gap: 12, marginTop: 6 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={cardStyle}>
            <span className="placeholder col-6" style={{ height: 14, borderRadius: 4 }} />
            <span className="placeholder col-9" style={{ display: 'block', height: 10, marginTop: 8, borderRadius: 4 }} />
          </div>
        ))}
      </div>
      <div style={{ ...LABEL_STYLE, marginTop: 18 }}>Objective</div>
      <div className="placeholder-glow" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginTop: 6 }}>
        {[0, 1, 2].map((i) => (
          <div key={i} style={{ padding: '12px 14px', borderRadius: 10, border: '1px solid #334155', background: '#0a1120' }}>
            <span className="placeholder col-7" style={{ height: 14, borderRadius: 4 }} />
            <span className="placeholder col-10" style={{ display: 'block', height: 10, marginTop: 8, borderRadius: 4 }} />
          </div>
        ))}
      </div>
      <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 10 }}>Loading objectives...</div>
    </div>
  );
}

function ScaleStep({ objectives, selectedScale, onSelectScale }) {
  const cardStyle = (active) => ({
    flex: 1, padding: '14px 16px', borderRadius: 10, border: `1px solid ${active ? CYAN : '#334155'}`,
    background: active ? 'rgba(34,211,238,0.08)' : '#0a1120', textAlign: 'left',
  });
  const nanoComingSoon = objectives.filter((o) => o.scale === 'nano' && o.status === 'coming_soon');
  const microComingSoon = objectives.filter((o) => o.scale === 'micro' && o.status === 'coming_soon');
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={LABEL_STYLE}>Scale</div>
      <div style={{ display: 'flex', gap: 12, marginTop: 6 }}>
        {/* A real button, same pattern as the live objective cards below
            (native <button>: cursor pointer, keyboard focusable, role
            "button" implicit): macro is the only live scale, so selecting it
            is idempotent, but it's genuinely clickable rather than a
            pointer cursor with nothing behind it. */}
        <button
          type="button"
          onClick={() => onSelectScale('macro')}
          style={{ ...cardStyle(selectedScale === 'macro'), cursor: 'pointer', font: 'inherit', color: 'inherit' }}
        >
          <div style={{ color: CYAN, fontWeight: 700 }}>Macro</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>Real lattices you can print today.</div>
        </button>
        <div style={{ ...cardStyle(false), opacity: 0.5, cursor: 'not-allowed' }}>
          <div style={{ fontWeight: 700 }}>Nano</div>
          {nanoComingSoon.map((o) => <div key={o.id} style={{ fontSize: 11, color: '#64748b' }}>{o.label}</div>)}
        </div>
        <div style={{ ...cardStyle(false), opacity: 0.5, cursor: 'not-allowed' }}>
          <div style={{ fontWeight: 700 }}>Micro</div>
          {microComingSoon.map((o) => <div key={o.id} style={{ fontSize: 11, color: '#64748b' }}>{o.label}</div>)}
        </div>
      </div>
      <div style={{ fontSize: 12, color: '#64748b', marginTop: 6 }}>Nano and micro scales: Coming soon.</div>
    </div>
  );
}

const ELECTRICAL_CONDUCTIVITY_TOOLTIP = 'Not offered at this scale. Lattice geometry cannot improve bulk electrical conductivity, that is a property of the material itself. Electron and phonon transport engineering is a nano scale capability, coming later.';

function ObjectiveStep({ objectives, selectedId, onSelect }) {
  const live = objectives.filter((o) => o.scale === 'macro' && o.status === 'live');
  if (live.length === 0) return null;
  return (
    <div style={{ marginBottom: 18 }}>
      <div style={LABEL_STYLE}>Objective</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 10, marginTop: 6 }}>
        {live.map((o) => (
          <button
            key={o.id}
            onClick={() => onSelect(o.id)}
            style={{
              textAlign: 'left', padding: '12px 14px', borderRadius: 10, cursor: 'pointer',
              border: `1px solid ${selectedId === o.id ? CYAN : '#334155'}`,
              background: selectedId === o.id ? 'rgba(34,211,238,0.1)' : '#0a1120', color: '#e2e8f0',
            }}
          >
            <div style={{ fontWeight: 700, color: selectedId === o.id ? CYAN : '#e2e8f0' }}>{o.label}</div>
            <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{o.description}</div>
          </button>
        ))}
        {/* Not a real objective, not selectable: a static card (no onClick, no
            button role) so the spec's "greyed out with a tooltip" promise for
            electrical conductivity at macro scale is visibly kept rather than
            silently absent. */}
        <div
          title={ELECTRICAL_CONDUCTIVITY_TOOLTIP}
          style={{
            textAlign: 'left', padding: '12px 14px', borderRadius: 10,
            border: '1px solid #334155', background: '#0a1120', color: '#e2e8f0',
            opacity: 0.5, cursor: 'not-allowed',
          }}
        >
          <div style={{ fontWeight: 700 }}>Electrical conductivity</div>
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>{ELECTRICAL_CONDUCTIVITY_TOOLTIP}</div>
        </div>
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
      <span style={LABEL_STYLE}>{label}</span>
      <input
        type="number"
        value={value}
        onChange={onChange}
        style={{ width: 140, padding: '6px 8px', borderRadius: 6, background: '#0a1120', border: '1px solid #334155', color: '#e2e8f0' }}
      />
    </label>
  );
}

function ConfigureStep({ objective, constraints, onChange }) {
  const set = (key) => (e) => onChange({ ...constraints, [key]: e.target.value });
  // v1 only optimizes a square panel (the GA searches footprint_x_mm alone),
  // while feasibility and reported metrics use the true rectangle. Offering
  // separate X/Y fields let a 100x50 request quietly return a 100x100 STL
  // with metrics that didn't match what was asked for. One field drives both
  // so what's requested is what's built; the server still rejects a mismatch.
  const setFootprint = (e) => onChange({ ...constraints, footprint_x_mm: e.target.value, footprint_y_mm: e.target.value });
  return (
    <div style={{ marginBottom: 12 }}>
      <div style={LABEL_STYLE}>Panel size</div>
      <div style={{ display: 'flex', gap: 12, marginTop: 6, flexWrap: 'wrap' }}>
        <NumberField label="Footprint (square, mm)" value={constraints.footprint_x_mm} onChange={setFootprint} />
        <NumberField label="Thickness min (mm)" value={constraints.thickness_lo_mm} onChange={set('thickness_lo_mm')} />
        <NumberField label="Thickness max (mm)" value={constraints.thickness_hi_mm} onChange={set('thickness_hi_mm')} />
        {objective.id === 'impact_protection' && (
          <>
            <NumberField label="Impact energy (J)" value={constraints.impact_energy_j} onChange={set('impact_energy_j')} />
            <NumberField label="Impactor diameter (mm)" value={constraints.impactor_diameter_mm} onChange={set('impactor_diameter_mm')} />
          </>
        )}
        {objective.id === 'heat_insulation' && (
          <NumberField label="Stiffness floor, optional (MPa)" value={constraints.min_stiffness_mpa} onChange={set('min_stiffness_mpa')} />
        )}
        {objective.id === 'cushioning' && (
          <NumberField label="Stiffness ceiling, optional (MPa)" value={constraints.stiffness_ceiling_mpa} onChange={set('stiffness_ceiling_mpa')} />
        )}
      </div>
    </div>
  );
}

function PropertyTable({ props, onEdit, readOnly, missingKeys }) {
  return (
    <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginTop: 8 }}>
      <thead>
        <tr style={{ color: '#94a3b8', textAlign: 'left' }}>
          <th style={{ padding: '4px 8px' }}>Property</th>
          <th style={{ padding: '4px 8px' }}>Value</th>
          <th style={{ padding: '4px 8px' }}>Source</th>
        </tr>
      </thead>
      <tbody>
        {CONTRACT_KEYS.map((key) => {
          const p = props[key];
          // Only a required, currently-missing field gets a fill-it-in hint:
          // non-required "not set" rows (e.g. speed of sound for an objective
          // that doesn't need it) aren't blocking anything, so no hint noise.
          const showFillHint = !readOnly && p?.value == null && missingKeys?.includes(key);
          return (
            <tr key={key} style={{ borderTop: '1px solid #1e293b' }}>
              <td style={{ padding: '6px 8px', color: '#cbd5e1' }}>{KEY_LABEL[key]}</td>
              <td style={{ padding: '6px 8px' }}>
                {readOnly ? (
                  <span style={{ color: p?.value != null ? '#e2e8f0' : '#64748b' }}>{p?.value != null ? p.value : 'not set'}</span>
                ) : (
                  <>
                    <input
                      type="number"
                      value={p?.value ?? ''}
                      placeholder="not set"
                      onChange={(e) => onEdit(key, e.target.value === '' ? null : parseFloat(e.target.value))}
                      style={{ width: 100, padding: '4px 6px', borderRadius: 4, background: '#0a1120', border: '1px solid #334155', color: '#e2e8f0' }}
                    />
                    {showFillHint && (
                      <div style={{ color: '#94a3b8', fontSize: 11, marginTop: 2 }}>
                        {DFT_DERIVABLE.includes(key) ? DFT_FIELD_HINT : MANUAL_FIELD_HINT}
                      </div>
                    )}
                  </>
                )}
              </td>
              <td style={{ padding: '6px 8px' }}>
                {p?.source && (
                  <span style={{ color: SOURCE_COLOR[p.source] || '#94a3b8', border: `1px solid ${SOURCE_COLOR[p.source] || '#94a3b8'}`, borderRadius: 999, padding: '1px 8px', fontSize: 11 }}>
                    {p.source}
                  </span>
                )}
              </td>
            </tr>
          );
        })}
      </tbody>
    </table>
  );
}

function GateRow({ readiness, onReady, onDftFirst, onEstimates, dftFirstChecking, latticeResumeChecking }) {
  if (!readiness) return null;
  // Review gate (Important, pre-deploy): while the wizard's mount effect is
  // still resolving whether an existing dft_elastic_tensor job should be
  // resumed (Firestore, then the registry fallback), this CTA renders
  // disabled instead of clickable in both branches below -- clicking it
  // during that window would submit a second, duplicate, expensive DFT job
  // race against the resume itself finding the real one.
  const dftFirstButton = dftFirstChecking
    ? (
      <button disabled style={{ ...PRIMARY_BTN, background: '#1e293b', color: '#94a3b8', cursor: 'not-allowed' }}>
        Checking for an existing run…
      </button>
    )
    : <button style={PRIMARY_BTN} onClick={onDftFirst}>Run DFT, then generate</button>;
  // D5 (durability audit 2026-08-20): the same review gate as dftFirstButton
  // above, for the wizard's OWN latticeJobs resume lookup -- while it's
  // pending, both CTAs that lead straight to submitLatticeJob render
  // disabled instead of clickable, so a click can't race the resume itself
  // finding an already-running GA job for this candidate and fire a second,
  // duplicate, expensive one.
  const checkingButton = (style) => (
    <button disabled style={{ ...style, background: '#1e293b', color: '#94a3b8', border: 'none', cursor: 'not-allowed' }}>
      Checking for an existing run…
    </button>
  );
  const generateButton = latticeResumeChecking
    ? checkingButton(PRIMARY_BTN)
    : <button style={PRIMARY_BTN} onClick={onReady}>Generate lattice</button>;
  const estimatesButton = latticeResumeChecking
    ? checkingButton(SECONDARY_BTN)
    : <button style={SECONDARY_BTN} onClick={onEstimates}>Generate with estimates</button>;
  if (readiness.missing.length > 0) {
    // Was previously a dead end: this branch rendered the red line below and
    // no buttons at all, so a candidate with, say, only a cached single-point
    // (density but no bulk modulus, hence youngs_modulus_gpa and everything
    // buildLatticeProps derives from it come back missing rather than
    // estimate-grade) had no path forward. If any missing or estimate-grade
    // required prop is something the elastic tensor DFT job can compute,
    // offer the same "Run DFT, then generate" CTA the estimate-grade branch
    // below uses; DFT_COMPLETE derives estimate defaults for anything still
    // missing afterward (see the dft_running effect and
    // latticeReadiness.deriveEstimateDefaults) so this always makes
    // progress. Otherwise every gap needs a person to type it in.
    const blockedKeys = [...readiness.missing, ...readiness.estimateGrade];
    const anyDftDerivable = blockedKeys.some((k) => DFT_DERIVABLE.includes(k));
    const missingLabels = readiness.missing.map((k) => KEY_LABEL[k] || k).join(', ');
    return (
      <div style={{ marginTop: 12 }}>
        <div style={{ color: '#f87171', fontSize: 13 }}>
          Missing properties: {missingLabels}. {anyDftDerivable
            ? 'Fill them by running the DFT job below, or by entering values directly in the property table above: typed values count as user provided.'
            : 'These need to be entered manually in the property table above: typed values count as user provided.'}
        </div>
        {anyDftDerivable && (
          <div style={{ marginTop: 10 }}>
            {dftFirstButton}
            <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 6 }}>{DFT_JOB_EXPLANATION}</div>
          </div>
        )}
      </div>
    );
  }
  if (readiness.ready) {
    return (
      <div style={{ marginTop: 16 }}>
        {generateButton}
      </div>
    );
  }
  return (
    <div style={{ marginTop: 16, display: 'flex', gap: 10 }}>
      {dftFirstButton}
      {estimatesButton}
    </div>
  );
}

function ConsentDialog({ estimateGrade, onProceed, onCancel }) {
  return (
    <div style={{ marginTop: 16, padding: 16, borderRadius: 10, border: `1px solid ${SOURCE_COLOR.estimate}`, background: 'rgba(245,158,11,0.08)' }}>
      <div style={{ color: SOURCE_COLOR.estimate, fontWeight: 700, marginBottom: 6 }}>Confirm estimate-grade inputs</div>
      <div style={{ fontSize: 13, color: '#e2e8f0', marginBottom: 8 }}>
        These values are estimates and could be off by 2 to 3 times. The optimization will spend real compute on them. Recommended: run the DFT job first.
      </div>
      <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 12 }}>
        Estimate-grade: {estimateGrade.map((k) => KEY_LABEL[k] || k).join(', ')}
      </div>
      <div style={{ display: 'flex', gap: 10 }}>
        <button style={PRIMARY_BTN} onClick={onProceed}>Proceed anyway</button>
        <button style={SECONDARY_BTN} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function ProgressView({ phase, progress, startedAt }) {
  const elapsed = useElapsed(startedAt);
  return (
    <div style={{ marginTop: 16 }}>
      {phase === 'dft_running' && (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>Computing elastic tensor by DFT, this takes a while.</div>
      )}
      {phase === 'generating' && (
        <div style={{ color: '#94a3b8', fontSize: 13 }}>
          {progress ? `Generation ${progress.generation}, best fitness ${round(progress.best_fitness)}.` : 'Starting the optimizer.'}
        </div>
      )}
      {elapsed && <div style={{ color: '#94a3b8', fontSize: 12, marginTop: 6 }}>Running for {elapsed}.</div>}
    </div>
  );
}

function ResultPanel({ result, objectiveId, baseUrl, jobId, props }) {
  const [downloadError, setDownloadError] = useState(null);
  if (!result) return null;
  const stlUrl = latticeStlUrl(baseUrl, jobId);
  const sentenceFn = HEADLINE_SENTENCES[objectiveId];
  const headline = sentenceFn ? sentenceFn(result.headline_metrics || {}) : null;

  const download = async () => {
    setDownloadError(null);
    try {
      const res = await authedFetch(stlUrl);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `lattice_${jobId}.stl`;
      a.click();
      // Defer the revoke so the click has a chance to start the save before
      // the object URL is invalidated.
      setTimeout(() => URL.revokeObjectURL(url), 0);
    } catch (e) {
      setDownloadError('The STL download failed. Try again in a moment.');
    }
  };

  return (
    <div style={{ marginTop: 16 }}>
      <div style={{ color: '#4ade80', fontWeight: 700, fontSize: 15 }}>Generation complete</div>
      {headline && <div style={{ color: '#e2e8f0', fontSize: 14, marginTop: 6 }}>{headline}</div>}
      <div style={{ marginTop: 12 }}>
        <Suspense fallback={<div style={{ width: 380, height: 300, borderRadius: 8, background: '#0a1120', color: '#94a3b8', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 12 }}>Loading preview</div>}>
          <StlViewer url={stlUrl} />
        </Suspense>
      </div>
      <button style={{ ...SECONDARY_BTN, marginTop: 10 }} onClick={download}>Download STL</button>
      {downloadError && <div style={{ color: '#f87171', fontSize: 12, marginTop: 8 }}>{downloadError}</div>}

      <div style={{ marginTop: 18 }}>
        <div style={{ color: CYAN, fontWeight: 700, fontSize: 13 }}>Methods</div>
        {result.methods?.physics_basis && (
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 4 }}>
            {PHYSICS_BASIS_TEXT[result.methods.physics_basis] || result.methods.physics_basis}
          </div>
        )}
        {result.methods?.scaling_constants && (
          <table style={{ fontSize: 11, marginTop: 8, color: '#cbd5e1' }}>
            <tbody>
              {Object.entries(result.methods.scaling_constants).map(([k, v]) => (
                <tr key={k}><td style={{ padding: '2px 8px 2px 0', color: '#94a3b8' }}>{k}</td><td>{String(v)}</td></tr>
              ))}
            </tbody>
          </table>
        )}
        <PropertyTable props={props} readOnly />
        {result.estimate_grade_inputs?.length > 0 && (
          <div style={{ color: SOURCE_COLOR.estimate, fontSize: 12, marginTop: 8 }}>
            This run used estimate-grade inputs for: {result.estimate_grade_inputs.map((k) => KEY_LABEL[k] || k).join(', ')}.
          </div>
        )}
      </div>
    </div>
  );
}
